// MainPage.js
import React, { useState, useEffect } from 'react';
import { useNavigate } from "react-router-dom";
import { Pickaxe, Twitter, AlertTriangle, Brain, ChevronRight, Search, Star, TrendingUp, Calendar, Activity, ScanSearch, DollarSign, Zap, Info, MessageSquare, Smartphone, X, Download, Mail, Globe, User, Sun, Moon, Clock } from 'lucide-react';
import { useTheme } from '../contexts/ThemeContext';
import SearchModal from './SearchModal/SearchModal'; // 새로운 SearchModal 컴포넌트 import
import { AnimatedCircularProgress } from './Progress/AnimatedCircularProgress';
import FullAnalysisModal from './FullAnalysisModal/FullAnalysisModal';
import './CoinMainPage.css';
import MiniTradingView from './TradingView/MiniTradingView';
// import HeatMapTradingView from './TradingView/HeatMapTradingView';
import InvestmentDisclaimer from './Common/InvestmentDisclaimer';
import ContactFooter from './Common/ContactFooter';
import { TwitterTweetEmbed } from 'react-twitter-embed';
import RankingSection from './Ranking/RankingSection';
import { getCoinLogoPath } from '../utils/coinLogo';
import RankingModal from './Ranking/RankingModal';
import OnboardingModal from './Onboarding/OnboardingModal';
import RedditBuzzBubble from './RedditBuzz/RedditBuzzBubble';
import PolymarketBuzz from './PolymarketBuzz/PolymarketBuzz';
import { getDashboardMainContent, getFavoriteList, getFearGreedIndex, getCoinRanking, getRefreshToken, isTokenExpired, isOAuthUser, getPlatform, initNativeToken, updateNativeRefreshToken, getAppVersion, isVersionBelow } from '../services/api';
import { useTimeFrame, TIMEFRAMES, SUPPORTED_TIMEFRAMES, TIMEFRAME_LABELS } from '../contexts/TimeFrameContext';
import AlertModal, { useAlert } from './Common/Alert';

const CoinMainPage = () => {
  const { theme, toggleTheme } = useTheme();
  const { alert, showAlert, closeAlert } = useAlert();
  const { timeFrame, setTimeFrame } = useTimeFrame();

  const [hasFavorites, setHasFavorites] = useState(false);
  const [isSearchModalOpen, setIsSearchModalOpen] = useState(false); // 검색 모달 상태 추가
  const [isFullAnalysisModalOpen, setIsFullAnalysisModalOpen] = useState(false);
  const [fearGreedData, setFearGreedData] = useState(null);
  const [mainContentData, setMainContentData] = useState(null);
  const [loading, setLoading] = useState(true);
  const [mainContentLoading, setMainContentLoading] = useState(true);


  const [favoriteList, setFavoriteList] = useState([]);
  const [favoriteLoading, setFavoriteLoading] = useState(false);
  const [showTooltip, setShowTooltip] = useState(null);
  const [showAppDownloadSheet, setShowAppDownloadSheet] = useState(false);
  const [xTweetData, setXTweetData] = useState([]);
  const [xTweetLoading, setXTweetLoading] = useState(false);
  const [rankingData, setRankingData] = useState(null);
  const [rankingLoading, setRankingLoading] = useState(true);
  const [showFullRanking, setShowFullRanking] = useState(false);
  const [showOnboarding, setShowOnboarding] = useState(false);

  const navigate = useNavigate();
  
  // 코인 로고 경로 — utils/coinLogo와 통일. 객체를 넘겨야 iconUrl 우선 적용.
  const getStockLogoPath = getCoinLogoPath;

  const handleClick = (symbol) => {
    navigate(`/coinDetail/${symbol}/`);
  };
  // 검색 모달 열기
  const openSearchModal = (e) => {
    // 이벤트 객체가 있는 경우 즉시 기본 동작 방지
    console.log(e, 'Search modal open triggered');
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      e.nativeEvent?.preventDefault();
      e.nativeEvent?.stopImmediatePropagation();
    }
    
    // 즉시 상태 업데이트 (setTimeout 제거)
    setIsSearchModalOpen(true);
    
    return false; // 추가적인 기본 동작 방지
  };

  // 검색 모달 닫기
  const closeSearchModal = (e) => {
    console.log(e, 'Search modal open triggered');
    if (e) {
      e.preventDefault();
      e.stopPropagation();
      e.nativeEvent?.preventDefault();
      e.nativeEvent?.stopImmediatePropagation();
    }

    setIsSearchModalOpen(false);
    return false; // 추가적인 기본 동작 방지
  };

  // 프로필 페이지로 이동
  const goToProfile = (e) => {
    e.preventDefault();
    navigate('/profile');
  };

  // 앱 다운로드 bottom sheet 닫기
  const closeAppDownloadSheet = () => {
    setShowAppDownloadSheet(false);
    localStorage.setItem('hasSeenAppPromo', 'true');
  };

  // 앱 스토어로 이동
  const goToAppStore = () => {
    const userAgent = navigator.userAgent || navigator.vendor || window.opera;
    
    if (/android/i.test(userAgent)) {
      // Android - Google Play Store
      window.open('https://play.google.com/store/apps/details?id=com.makemoneystudio.isf', '_self');
    } else if (/iPad|iPhone|iPod/.test(userAgent) && !window.MSStream) {
      // iOS - App Store
      window.open('https://apps.apple.com/app/6748879356', '_self');
    } else {
      // 기본적으로 Google Play Store로 이동
      window.open('https://play.google.com/store/apps/details?id=com.makemoneystudio.isf', '_self');
    }
    
    closeAppDownloadSheet();
  };


  // refreshToken 기준 인증 초기화
  const initAuth = async (isOAuthSuccess = false) => {
    console.log('[initAuth] start, isOAuthSuccess:', isOAuthSuccess);

    if (!isOAuthSuccess) {
      // 일반 진입: 네이티브에서 refreshToken 가져와 저장
      console.log('[initAuth] calling initNativeToken...');
      await initNativeToken();
      console.log('[initAuth] initNativeToken done');
    }

    const refreshToken = getRefreshToken();
    console.log('[initAuth] refreshToken:', refreshToken ? refreshToken.substring(0, 20) + '...' : null);
    console.log('[initAuth] isOAuthUser:', isOAuthUser());

    if (!isOAuthUser()) {
      console.log('[initAuth] not OAuth user → exit');
      return;
    }

    // OAuth 성공 직후: 서버 쿠키의 refreshToken을 앱에 저장
    if (isOAuthSuccess) {
      if (refreshToken) {
        console.log('[initAuth] calling updateNativeRefreshToken');
        updateNativeRefreshToken(refreshToken);
      } else {
        console.log('[initAuth] OAuth success but no refreshToken found!');
      }
    }

    console.log('[initAuth] calling fetchFavoriteList');
    await fetchFavoriteList();
    console.log('[initAuth] done');
  };

  // 즐겨찾기 목록 가져오기 함수
  const fetchFavoriteList = async () => {
    try {
      setFavoriteLoading(true);
      const data = await getFavoriteList();
      setFavoriteList(data.list || []);
      setHasFavorites(data.list && data.list.length > 0);
      console.log('Favorite list received:', data.list);
    } catch (error) {
      console.error('Error fetching favorite list:', error);
      setFavoriteList([]);
      setHasFavorites(false);
    } finally {
      setFavoriteLoading(false);
    }
  };

  // 공포 탐욕 지수 API 호출
  // 응답 스키마: { fearGreedValue: number, fearGreedClassification: string }
  const fetchFearGreedIndexData = async () => {
    try {
      setLoading(true);
      const data = await getFearGreedIndex();
      setFearGreedData(data);
    } catch (error) {
      console.error('Error fetching fear and greed index:', error);
    } finally {
      setLoading(false);
    }
  };

  // 대시보드 메인 컨텐츠 API 호출
  const fetchMainContent = async () => {
    try {
      setMainContentLoading(true);
      const data = await getDashboardMainContent();
      setMainContentData(data);
    } catch (error) {
      console.error('Error fetching main content:', error);
    } finally {
      setMainContentLoading(false);
    }
  };

  // X 트윗 섹션은 현재 비활성화 (API 미제공)
  const fetchXTweetData = async () => {};

  // 코인 랭킹 데이터 API 호출 (timeFrame 의존)
  const fetchRankingData = async (tf = timeFrame) => {
    try {
      setRankingLoading(true);
      const data = await getCoinRanking(tf);
      setRankingData(data);
    } catch (error) {
      console.error('Error fetching coin ranking:', error);
    } finally {
      setRankingLoading(false);
    }
  };

  // 신규 유저 온보딩 체크 (비활성화)
  const checkFirstVisitOnboarding = () => {
    // setShowOnboarding(true);
  };

  const handleOnboardingClose = () => {
    setShowOnboarding(false);
    localStorage.setItem('hasSeenOnboarding', 'true');
  };

  const handleKakaoShare = () => {
    const kakao = window.Kakao;
    if (!kakao) return;

    if (!kakao.isInitialized()) {
      kakao.init('3a612caf3635160cef3ec4542c0b8344');
    }

    const score = mainContentData?.score ?? '-';
    const summary = mainContentData?.aiSummary
      ? mainContentData.aiSummary.replace(/[#*_~`>]/g, '').trim().slice(0, 100) + '...'
      : '오늘의 AI 시장 분석을 확인하세요.';

    kakao.Share.sendDefault({
      objectType: 'feed',
      content: {
        title: `코인특공대 AI 시장 분석 · 오늘의 점수: ${score}점`,
        description: summary,
        imageUrl: `${window.location.origin}/social-share.png`,
        link: {
          webUrl: 'https://coin.mmstudio.co.kr',
          mobileWebUrl: 'https://coin.mmstudio.co.kr/appStore',
        },
      },
      buttons: [
        {
          title: '앱에서 보기',
          link: {
            webUrl: 'https://coin.mmstudio.co.kr',
            mobileWebUrl: 'https://coin.mmstudio.co.kr/appStore',
            // androidExecutionParams: `ticker=main`,
            // iosExecutionParams: `ticker=main`,
          },
        },
      ],
    });
  };

  // 웹 사용자 첫 방문 체크 및 앱 다운로드 유도
  const checkWebFirstVisit = () => {
    const platform = getPlatform();
    if (platform === 'ios' || platform === 'android') return;

    setTimeout(() => {
      const hasSeenAppPromo = localStorage.getItem('hasSeenAppPromo');
      if (!hasSeenAppPromo) {
        setShowAppDownloadSheet(true);
      }
    }, 4000);
  };

  // 각 섹션의 렌더링 함수들
  // const renderHeatmapSection = () => (
  //   <section className="section">
  //     <div className="favorites-grid">
  //       <div className="relative">
  //         <HeatMapTradingView></HeatMapTradingView>

  //         {/* 데이터 기준 안내 툴팁 */}
  //         <div className="absolute top-4 left-0 z-9999">
  //           <button
  //             className="relative"
  //             onMouseEnter={() => setShowTooltip('heatmap')}
  //             onMouseLeave={() => setShowTooltip(null)}
  //             onClick={() => setShowTooltip(showTooltip === 'heatmap' ? null : 'heatmap')}
  //           >
  //             <div className="w-5 h-5 bg-gray-600 bg-opacity-70 hover:bg-opacity-90 rounded-full flex items-center justify-center transition-all duration-200">
  //               <Info size={12} className="text-white" />
  //             </div>
  //           </button>
  //         </div>
  //       </div>
  //     </div>
  //   </section>
  // );

  const renderAiAnalysisSection = () => (
    <div className="feature-section">
      <div className="flex items-center justify-between gap-1 mb-1">
        {/* 좌측: 아이콘 + 제목 + 설명글 */}
        <div className="flex-1">
          <div className="flex items-center gap-2 mb-2">
            <div className="feature-icon">
              <Brain size={20} />
            </div>
            <h2 className="text-xm font-bold text-black">시장 분석</h2>
          </div>
          <p className="text-xs text-gray-600 pl-1">
            국제 정세와 시장 심리를 한눈에 파악하세요
          </p>
        </div>

        {/* 우측: 스코어 */}
        <AnimatedCircularProgress
            key={`main-score-${mainContentData?.score || 0}`}
            score={mainContentData?.score || 0}
            size={76}
            delay={300}
          />
      </div>

      {mainContentLoading ? (
        <div className="ai-summary-card loading animate-pulse">
          <div className="h-3 bg-gray-100 rounded mb-3 w-20"></div>
          <div className="h-3 bg-gray-100 rounded mb-2"></div>
          <div className="h-3 bg-gray-100 rounded mb-2"></div>
          <div className="h-3 bg-gray-100 rounded w-2/3"></div>
        </div>
      ) : (
        <div className="ai-summary-card">
          <div className="ai-summary-header">
            <div className="flex items-center gap-1.5">
              <span className="ai-badge"><Zap size={9} />AI</span>
              <span className="ai-summary-title">핵심 요약</span>
            </div>
            {/* <button
              className="kakao-share-btn"
              onClick={handleKakaoShare}
              disabled={!mainContentData?.score}
            >
              <img
                src="https://developers.kakao.com/assets/img/about/logos/kakaotalksharing/kakaotalk_sharing_btn_medium.png"
                alt="카카오톡 공유"
                style={{ width: 14, height: 14 }}
              />
              공유
            </button> */}
          </div>
          <p className="ai-summary-text">
            {mainContentData?.aiSummary || '시장 분석 데이터를 불러오는 중입니다...'}
          </p>
        </div>
      )}

      {!mainContentLoading && (
        <button className="view-full-button mb-4"
            onClick={() => setIsFullAnalysisModalOpen(true)}>
            <span>전체 분석 보기</span>
            <ChevronRight size={16} />
        </button>
      )}

      </div>
  );

  const renderTimeFrameToggle = () => (
    <div className="feature-section">
      <div className="flex items-center gap-1 mb-2">
        <div className="feature-icon">
          <Clock size={20} />
        </div>
        <h2 className="text-xm font-bold text-black">분석 주기</h2>
      </div>
      <p className="text-xs text-gray-600 pl-1 mb-3">
        선택한 주기는 앱 전체에 적용됩니다. 회색 항목은 아직 백엔드가 지원하지 않아요.
      </p>

      <div className="tf-pills" role="tablist" aria-label="분석 주기 선택">
        {TIMEFRAMES.map((tf) => {
          const active = timeFrame === tf;
          const supported = SUPPORTED_TIMEFRAMES.includes(tf);
          return (
            <button
              key={tf}
              type="button"
              role="tab"
              aria-selected={active}
              aria-disabled={!supported}
              disabled={!supported}
              onClick={() => supported && setTimeFrame(tf)}
              title={supported ? '' : '백엔드 미지원'}
              className={`tf-pill ${active ? 'tf-pill--active' : ''}`}
            >
              {TIMEFRAME_LABELS[tf] || tf}
            </button>
          );
        })}
      </div>
    </div>
  );

  const renderRankingSection = () => (
    !rankingLoading && rankingData && (
      <RankingSection
        rankingData={rankingData}
        onStockClick={handleClick}
        onOpenRanking={() => setShowFullRanking(true)}
      />
    )
  );

  const renderFavoritesSection = () => (
    <div className="feature-section">
      <div className="flex items-center gap-1 mb-2">
        <div className="feature-icon">
          <ScanSearch size={20} />
        </div>
        <h2 className="text-xm font-bold text-black">관심 기업</h2>
      </div>
      <p className="text-xs text-gray-600 pl-1 mb-3">
        즐겨찾기한 주식들의 실시간 가격과 변동률을 확인하고 빠른 투자 결정을 내리세요.
      </p>

      {favoriteLoading ? (
        <div className="card animate-pulse">
          <div className="flex justify-between items-center py-3 border-b border-gray-100">
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded mb-1 w-16"></div>
              <div className="h-3 bg-gray-200 rounded w-24"></div>
              <div className="h-20 bg-gray-200 rounded mt-2"></div>
            </div>
          </div>
          <div className="flex justify-between items-center py-3">
            <div className="flex-1">
              <div className="h-4 bg-gray-200 rounded mb-1 w-16"></div>
              <div className="h-3 bg-gray-200 rounded w-24"></div>
              <div className="h-20 bg-gray-200 rounded mt-2"></div>
            </div>
          </div>
        </div>
      ) : hasFavorites && favoriteList.length > 0 ? (
        <div className="card">
          {favoriteList.map((favorite, index) => (
            <div
              key={favorite.userFavoriteTickerNo || index}
              className={`flex justify-between items-center py-3 cursor-pointer hover:bg-gray-50 transition-colors ${
                index < favoriteList.length - 1 ? 'border-b border-gray-100' : ''
              }`}
              onClick={() => handleClick(favorite.ticker)}
            >
              <div className="flex items-center">

                <div>
                  <div className="flex items-start">
                    <div className="stock-icon"
                        style={{ width: '60px', height: '60px'}}>
                      <img
                        src={getStockLogoPath(favorite)}
                        alt={favorite.nameKr || favorite.ticker}
                      />
                      <TrendingUp size={20} className="fallback-icon" />
                    </div>
                    <div className="stock-info">
                      <div className="stock-symbol">{favorite.ticker}</div>
                        <div className="stock-name">
                          {favorite.nameKr && <div>{favorite.nameKr}</div>}
                          {favorite.nameEn && <div>{favorite.nameEn}</div>}
                        </div>
                    </div>
                  </div>
                  <div className="mt-2">
                    <MiniTradingView symbol={`${favorite.ticker}`} />
                  </div>
                </div>
              </div>
            </div>
          ))}
        </div>
      ) : (
        <div className="add-favorites-card">
          <Star className="add-star text-blue-400" />
          <h3 className="add-favorites-title">즐겨찾기 추가</h3>
          <p className="add-favorites-desc">
            관심 있는 주식을 즐겨찾기에 추가하여 빠르게 확인하세요
          </p>
          <button
            className="view-full-button"
            onClick={(e) => {
              e.preventDefault();
              e.stopPropagation();
              openSearchModal(e);
            }}
          >
            <Search size={16} />
            <span>주식 검색</span>
          </button>
        </div>
      )}
    </div>
  );

  const renderFearGreedSection = () => (
    <div className="feature-section">

      <div className="flex items-center gap-1 mb-2">
        <div className="feature-icon">
          <Activity size={20} />
        </div>
        <h2 className="text-xm font-bold text-black">공포 탐욕 지수</h2>
      </div>
      <p className="text-xs text-gray-600 pl-1 mb-3">
        시장 투자자들의 감정 상태를 측정하여 현재 시장이 과매수인지 과매도인지 파악하세요.
      </p>

      <div className="card">
        <div className="flex justify-between items-center mb-4">
          <div>
            <h3 className="text-base font-semibold text-gray-900">
              {loading ? 'Loading...' : (() => {
                const score = fearGreedData?.fearGreedValue ?? 0;
                if (score <= 25) return '현재 공포탐욕지수: 극심한 공포';
                if (score <= 45) return '현재 공포탐욕지수: 공포';
                if (score <= 55) return '현재 공포탐욕지수: 중립';
                if (score <= 75) return '현재 공포탐욕지수: 탐욕';
                return '현재 공포탐욕: 과한 탐욕';
              })()}
            </h3>
            <p className="text-xs text-gray-500 mt-1">
              {loading ? 'Fetching data...' : (() => {
                const score = fearGreedData?.fearGreedValue ?? 0;
                if (score <= 25) return '투자자들이 매우 공포에 빠져 있습니다';
                if (score <= 45) return '투자자들이 공포를 느끼고 있습니다';
                if (score <= 55) return '투자자들이 중립적인 상태입니다';
                if (score <= 75) return '투자자들이 탐욕을 느끼고 있습니다';
                return '투자자들이 매우 탐욕적입니다';
              })()}
            </p>
          </div>
          <div className="flex items-center">
            <span className={`text-2xl font-bold ml-2 ${loading ? 'text-gray-900' : (() => {
                const score = fearGreedData?.fearGreedValue ?? 0;
                if (score <= 25) return 'text-red-500';
                if (score <= 45) return 'text-orange-500';
                if (score <= 55) return 'text-yellow-500';
                if (score <= 75) return 'text-green-500';
                return 'text-green-700';
              })()}`}>
              {loading || fearGreedData?.fearGreedValue == null ? 'Loading...' : fearGreedData.fearGreedValue.toFixed(0)}
            </span>
          </div>
        </div>

        <div className="w-full h-2 bg-gray-200 rounded-full overflow-hidden mb-2 relative">
          <div className="flex h-full">
            <div style={{ width: '25%' }} className="bg-red-500"></div>
            <div style={{ width: '20%' }} className="bg-orange-500"></div>
            <div style={{ width: '10%' }} className="bg-yellow-400"></div>
            <div style={{ width: '20%' }} className="bg-green-500"></div>
            <div style={{ width: '25%' }} className="bg-green-700"></div>
          </div>
          {!loading && fearGreedData?.fearGreedValue != null && (
            <div
              className="absolute top-0 w-1 h-full bg-black z-10"
              style={{
                left: `${fearGreedData.fearGreedValue}%`,
                transform: 'translateX(-50%)'
              }}
            />
          )}
        </div>

        <div className="flex justify-between mb-4 text-xs text-gray-500">
          <span>극심한 공포</span>
          <span>공포</span>
          <span>중립</span>
          <span>탐욕</span>
          <span>과한 탐욕</span>
        </div>

        {fearGreedData?.fearGreedClassification && (
          <div className="text-xs text-gray-400 text-right">
            {fearGreedData.fearGreedClassification}
          </div>
        )}
      </div>
    </div>
  );

  const renderTwitterSection = () => (
    <div className="feature-section">
      <div className="flex items-center gap-1 mb-2">
        <div className="feature-icon">
          <Twitter size={20} />
        </div>
        <h2 className="text-xm font-bold text-black">주요 인물 SNS</h2>
      </div>
      <p className="text-xs text-gray-600 pl-1 mb-3">
        주요 인물들의 X(트위터)를 통해 최신 시장 동향과 리스크를 실시간으로 확인하세요.
      </p>

      {xTweetLoading ? (
        <div className="bg-white rounded-lg p-6 animate-pulse">
          <div className="flex items-center gap-3 mb-4">
            <div className="w-10 h-10 bg-gray-200 rounded-full"></div>
            <div>
              <div className="h-4 bg-gray-200 rounded w-24 mb-2"></div>
              <div className="h-3 bg-gray-200 rounded w-16"></div>
            </div>
          </div>
          <div className="space-y-2">
            <div className="h-4 bg-gray-200 rounded"></div>
            <div className="h-4 bg-gray-200 rounded w-4/5"></div>
            <div className="h-4 bg-gray-200 rounded w-3/5"></div>
          </div>
        </div>
      ) : xTweetData.length > 0 ? (
        <div className="space-y-4">
          {xTweetData.map((tweet, index) => (
            <TwitterTweetEmbed
              key={`${tweet.xinfoNo}-${theme}`}
              tweetId={tweet.xid}
              options={{ theme: theme === 'dark' ? 'dark' : 'light' }}
            />
          ))}
        </div>
      ) : (
        <div className="bg-white rounded-lg p-6 text-center">
          <div className="text-gray-500 mb-2">트윗을 불러올 수 없습니다</div>
          <button
            onClick={fetchXTweetData}
            className="text-blue-600 text-sm hover:underline"
          >
            다시 시도
          </button>
        </div>
      )}
    </div>
  );

  useEffect(() => {
    document.title = '코인특공대 — AI 코인 분석';
    return () => { document.title = '코인특공대'; };
  }, []);

  // 컴포넌트 마운트 시 API 호출
  useEffect(() => {
    // OAuth 성공 여부 URL 파라미터 체크
    const params = new URLSearchParams(window.location.search);
    const isOAuthSuccess = params.get('successYn') === 'Y';
    console.log('[useEffect] mount, url:', window.location.href, 'isOAuthSuccess:', isOAuthSuccess);

    // 강제 업데이트 체크 (iOS 2.0.0 미만)
    if (getPlatform() === 'ios') {
      getAppVersion().then((version) => {
        if (isVersionBelow(version, '1.0.0')) {
          showAlert('error', `현재 버전(${version})은 더 이상 지원되지 않아요.\n최신 버전으로 업데이트해주세요.`, {
            autoClose: false,
            confirmLabel: '업데이트',
            cancelable: false,
            dismissable: false,
            onConfirm: () => {
              if (window.webkit?.messageHandlers?.openExternalUrl) {
                window.webkit.messageHandlers.openExternalUrl.postMessage('https://apps.apple.com/app/6748879356'); // iOS
              }
            },
          });
        }
      });
    }

    initAuth(isOAuthSuccess);
    checkFirstVisitOnboarding();
    checkWebFirstVisit();
    fetchFearGreedIndexData();
    fetchMainContent();
    fetchXTweetData();
    // 랭킹은 timeFrame 의존 effect가 마운트 시 자동으로 한 번 호출하므로 여기서 중복 호출하지 않는다.
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, []);

  // timeFrame 변경 시 (그리고 초기 마운트 시) 랭킹 재조회
  useEffect(() => {
    fetchRankingData(timeFrame);
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [timeFrame]);


  return (
    <div className="main-container">
      {/* 상단 헤더 */}
      <header className="header">
        <div className="header-content">
          <div className="header-row">
            <div className="logo-container">
              <div className="header-brand">
                <span className="header-brand-name">코인특공대</span>
                <span className="header-brand-sub">AI Crypto Analysis</span>
              </div>
            </div>
            <div className="header-actions">
              <div
                className="icon-button"
                role="button"
                tabIndex={0}
                onMouseDown={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onTouchStart={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                }}
                onClick={(e) => {
                  e.preventDefault();
                  e.stopPropagation();
                  openSearchModal(e);
                }}
                style={{ cursor: 'pointer' }}
              >
                <Search size={16} />
              </div>
              <button type="button" className="icon-button" onClick={toggleTheme}>
                {theme === 'dark' ? <Sun size={16} /> : <Moon size={16} />}
              </button>
              {getPlatform() === 'web' && (
                <button type="button" className="icon-button" onClick={() => navigate('/community')}>
                  <MessageSquare size={16} />
                </button>
              )}
              <button type="button" className="icon-button" onClick={goToProfile}>
                <User size={16} />
              </button>
            </div>
          </div>
        </div>
      </header>

      {/* 메인 컨텐츠 */}
      <main className="main-content">

      {/* 투자 면책 조항 */}
      <InvestmentDisclaimer />

      {/* 시장 분석 */}
      {renderAiAnalysisSection()}


      {/* 분석 주기 (1d / 8h) — Context 전역 — 잠시 숨김 */}
      {/* {renderTimeFrameToggle()} */}

      {/* 코인 랭킹 */}
      {renderRankingSection()}

      {/* 관심 기업 */}
      {renderFavoritesSection()}

      {/* Reddit 트렌딩 — 백엔드 미제공으로 비활성화 (Swagger에 reddit 엔드포인트 없음) */}
      {/* <RedditBuzzBubble /> */}

      {/* Polymarket 예측 */}
      <PolymarketBuzz />

      {/* 공포 탐욕 지수 */}
      {renderFearGreedSection()}

      {/* X(트위터) */}
      {renderTwitterSection()}

{/* 다가오는 이벤트 섹션 */}
{/* <div className="feature-section">
  <div className="feature-icon">
    <Calendar size={24} />
  </div>
  <h2 className="text-xl font-bold mb-2 text-black">중요한 이벤트</h2>
  <p className="text-sm text-gray-600 mb-4">
    다가오는 중요한 경제 이벤트와 실적 발표 일정을 미리 확인하고 대비하세요.
  </p>
  
  <div className="card">
    <div className="flex justify-between mb-3">
      <div className="flex-1">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">
          Quadruple Witching Day
        </h4>
        <p className="text-xs text-gray-600 leading-relaxed">
          The simultaneous expiration of stock index futures, stock index options, 
          stock options, and single stock futures on the third Friday of March, June, 
          September, and December...
        </p>
      </div>
    </div>
    <div className="flex justify-between items-center text-xs">
      <span className="text-gray-400">June 21, 2025</span>
      </div>
  </div>

  <div className="card mt-4">
    <div className="flex justify-between mb-3">
      <div className="flex-1">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">
          Fed Interest Rate Decision
        </h4>
        <p className="text-xs text-gray-600 leading-relaxed">
          The Federal Open Market Committee (FOMC) announces its decision 
          on interest rates. Market expects rates to remain unchanged at 5.25-5.50%...
        </p>
      </div>
    </div>
    <div className="flex justify-between items-center text-xs">
      <span className="text-gray-400">June 26, 2025</span>
      </div>
  </div>
</div> */}

{/* 주식 용어 팁 섹션 */}
{/* <div className="feature-section">
  <div className="feature-icon">
    <BookOpen size={24} />
  </div>
  <h2 className="text-xl font-bold mb-2 text-black">투자 지식</h2>
  <p className="text-sm text-gray-600 mb-4">
    주식 투자에 필요한 핵심 용어와 개념을 쉽게 이해하고 투자 실력을 향상시키세요.
  </p>
  
  <div className="card">
    <div className="flex justify-between mb-3">
      <div className="flex-1">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">
          What is Quadruple Witching?
        </h4>
        <p className="text-xs text-gray-600 leading-relaxed">
          "Quadruple Witching" refers to the simultaneous expiration of stock index futures, 
          stock index options, stock options, and single stock futures. This happens four times 
          a year on the third Friday of March, June, September, and December. These days typically 
          see increased trading volume and volatility...
        </p>
      </div>
    </div>
    <div className="flex justify-end">
      <button className="text-xs text-blue-600 hover:underline">Read more</button>
    </div>
  </div>

  <div className="card mt-4">
    <div className="flex justify-between mb-3">
      <div className="flex-1">
        <h4 className="text-sm font-semibold text-gray-900 mb-2">
          Understanding Price-to-Earnings (P/E) Ratio
        </h4>
        <p className="text-xs text-gray-600 leading-relaxed">
          The Price-to-Earnings (P/E) ratio is a valuation metric that compares a company's 
          current share price to its earnings per share (EPS). A high P/E ratio could mean 
          a stock is overvalued or that investors expect high growth rates in the future...
        </p>
      </div>
    </div>
    <div className="flex justify-end">
      <button className="text-xs text-blue-600 hover:underline">Read more</button>
    </div>
  </div>
</div> */}
      </main>

      {/* 온보딩 모달 */}
      <OnboardingModal
        isOpen={showOnboarding}
        onClose={handleOnboardingClose}
      />

      {/* 랭킹 모달 */}
      <RankingModal
        isOpen={showFullRanking}
        onClose={() => setShowFullRanking(false)}
        rankingData={rankingData}
        onStockClick={handleClick}
      />

      {/* 모달 */}
      <FullAnalysisModal
        isOpen={isFullAnalysisModalOpen} 
        onClose={() => setIsFullAnalysisModalOpen(false)} 
        analysisText={mainContentData?.mainContent || "시장 분석 데이터를 불러오는 중입니다..."}
        stockSymbol="시장 전체"
        reportType="market"
      />

      {/* 검색 모달 */}
      <SearchModal
        isOpen={isSearchModalOpen}
        onClose={closeSearchModal}
        onStockClick={handleClick}
      />
      
      {/* 앱 다운로드 유도 Bottom Sheet */}
      {showAppDownloadSheet && (
        <div className="app-promo-overlay" onClick={closeAppDownloadSheet}>
          <div className="app-promo-sheet" onClick={e => e.stopPropagation()}>
            {/* 드래그 핸들 */}
            <div className="app-promo-handle" />

            {/* 헤더: 로고 + 닫기 */}
            <div className="app-promo-header">
              <div className="app-promo-logo-wrap">
                <img src="/logo.png" alt="코인특공대" className="app-promo-logo" />
              </div>
              <button onClick={closeAppDownloadSheet} className="app-promo-close">
                <X size={16} />
              </button>
            </div>

            {/* 본문 */}
            <div className="app-promo-body">
              <h3 className="app-promo-title">앱에서 더 나은 경험을</h3>
              <p className="app-promo-subtitle">AI 투자 분석을 더 빠르고 편리하게</p>

              <div className="app-promo-features">
                <div className="app-promo-feature">
                  <div className="app-promo-feature-icon app-promo-icon--blue">
                    <Star size={14} fill="currentColor" />
                  </div>
                  <span>관심 종목 즐겨찾기</span>
                </div>
                <div className="app-promo-feature">
                  <div className="app-promo-feature-icon app-promo-icon--purple">
                    <Zap size={14} />
                  </div>
                  <span>더 빠른 로딩 속도</span>
                </div>
                <div className="app-promo-feature">
                  <div className="app-promo-feature-icon app-promo-icon--green">
                    <MessageSquare size={14} />
                  </div>
                  <span>커뮤니티 이용 가능</span>
                </div>
              </div>
            </div>

            {/* 액션 버튼 */}
            <div className="app-promo-actions">
              <button onClick={goToAppStore} className="app-promo-btn-primary">
                <Download size={18} />
                앱 다운로드
              </button>
              <button onClick={closeAppDownloadSheet} className="app-promo-btn-secondary">
                나중에 하기
              </button>
            </div>
          </div>
        </div>
      )}

      <ContactFooter />

      <AlertModal alert={alert} onClose={closeAlert} />
    </div>
  );
};

export default CoinMainPage;
